/** User, API-key and door-registry administration.
 *
 * These endpoints existed from the beginning and the console never called one of them
 * (docs/KNOWN-PROBLEMS.md finding 18, docs/FRONTEND-AUDIT.md F-P1-1 and F-P1-2). Creating a
 * colleague's login, revoking a compromised key, registering a door or correcting a mistyped
 * controller address all meant dropping to curl. For a security console, credential
 * administration being API-only is an operational gap, not a missing nicety.
 *
 * Every one is `manage_system` (or `manage_doors`) on the server. The UI gates them too, but
 * that is a courtesy - the API refuses them regardless of what this console chooses to render.
 */
import { api } from "./client.js";
import type {
  ApiKey,
  CreateApiKeyInput,
  CreatedApiKey,
  Door,
  DoorCreateInput,
  DoorUpdateInput,
  User,
  CreateUserInput,
} from "../types/api.js";

// --- users ----------------------------------------------------------------------------------

export const listUsers = () => api.get<User[]>("/users");

export const createUser = (input: CreateUserInput) => api.post<User>("/users", input);

/** Deactivation, not deletion. A deleted user would take the meaning of every audit row that
 * names them with it; an inactive one cannot sign in and stays readable in the history. */
export const setUserActive = (userId: string, isActive: boolean) =>
  api.patch<User>(`/users/${encodeURIComponent(userId)}/active`, { isActive });

// --- API keys -------------------------------------------------------------------------------

export const listApiKeys = () => api.get<ApiKey[]>("/api-keys");

export const createApiKey = (input: CreateApiKeyInput) =>
  api.post<CreatedApiKey>("/api-keys", {
    name: input.name,
    role: input.role,
    ...(input.expiresAt !== undefined ? { expiresAt: input.expiresAt } : {}),
  });

export const revokeApiKey = (keyId: string) =>
  api.delete<void>(`/api-keys/${encodeURIComponent(keyId)}`);

// --- door registry --------------------------------------------------------------------------

export const createDoor = (input: DoorCreateInput) => api.post<Door>("/doors", input);

export const updateDoor = (doorId: string, input: DoorUpdateInput) =>
  api.patch<Door>(`/doors/${encodeURIComponent(doorId)}`, input);

/** The API refuses this while an override is active, because deleting the row would lose the
 * only record that a door is standing in one. Surface that reason rather than a generic error. */
export const deleteDoor = (doorId: string) =>
  api.delete<void>(`/doors/${encodeURIComponent(doorId)}`);
